import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select } from "../components/DesignSystem";
import type { BeautyAppointment } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export function BeautyScreen() {
  const { workspace, loading, error, service } = usePlannerData();
  const [appointments, setAppointments] = React.useState<BeautyAppointment[] | null>(null);
  const [appointmentsLoading, setAppointmentsLoading] = React.useState(false);
  const [appointmentsError, setAppointmentsError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setAppointmentsLoading(true);
    setAppointmentsError(null);
    try {
      setAppointments(await service.beauty.list());
    } catch (err) {
      setAppointmentsError(errorMessage(err));
    } finally {
      setAppointmentsLoading(false);
    }
  }, [service]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading beauty" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const remove = async (appointment: BeautyAppointment) => {
    setActionError(null);
    try { await service.beauty.deleteAppointment(appointment.id); await load(); } catch (err) { setActionError(errorMessage(err)); }
  };

  return (
    <div>
      <PageHeader eyebrow="More" title="Beauty" actions={<Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add appointment</Button>} />
      <div className="pob-pad">
        {appointmentsLoading && !appointments && <LoadingState label="Loading beauty" />}
        {appointmentsError && <ErrorState message={appointmentsError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}
        {appointments && (
          <Card padding="0">
            <DataTable
              rows={appointments}
              emptyLabel="No appointments yet"
              columns={[
                { key: "personName", header: "Person" },
                { key: "service", header: "Service" },
                { key: "isTrial", header: "", width: 70, render: (row) => row.isTrial ? <Badge tone="accent">Trial</Badge> : null },
                { key: "providerName", header: "Provider", muted: true },
                { key: "time", header: "Time", width: 80, muted: true },
                { key: "location", header: "Location", muted: true },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={() => remove(row)}>Remove</Button> }
              ]}
            />
          </Card>
        )}
      </div>
      {adding && <AddAppointmentDialog onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await load(); }} />}
    </div>
  );
}

function AddAppointmentDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service, workspace } = usePlannerData();
  const [personId, setPersonId] = React.useState(workspace!.people[0]?.id || "");
  const [eventId, setEventId] = React.useState("");
  const [service_, setService_] = React.useState("");
  const [providerId, setProviderId] = React.useState("");
  const [time, setTime] = React.useState("");
  const [isTrial, setIsTrial] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!personId || !service_.trim()) { setFormError("Choose a person and enter a service."); return; }
    try {
      await service.beauty.createAppointment({ personId, eventId: eventId || undefined, service: service_.trim(), providerId: providerId || undefined, time: time || undefined, isTrial }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog title="Add beauty appointment" onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Person"><Select value={personId} onChange={(e) => setPersonId(e.target.value)} options={workspace!.people.map((p) => ({ value: p.id, label: p.name }))} /></Field>
        <Field label="Service"><Input value={service_} onChange={(e) => setService_(e.target.value)} placeholder="Hair & makeup" /></Field>
        <Field label="Celebration (optional — leave blank for a trial)"><Select value={eventId} placeholder="No celebration (trial)" onChange={(e) => setEventId(e.target.value)} options={workspace!.events.map((ev) => ({ value: ev.id, label: ev.name }))} /></Field>
        <Field label="Provider (optional)"><Select value={providerId} placeholder="None" onChange={(e) => setProviderId(e.target.value)} options={workspace!.vendors.map((v) => ({ value: v.id, label: v.name }))} /></Field>
        <Field label="Time"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", font: "var(--type-body-sm)" }}>
          <input type="checkbox" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />
          This is a trial
        </label>
      </div>
    </Dialog>
  );
}
